import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bone } from "@/components/layout/PageSkeleton";
import {
  STEP_UP_PURPOSE_TEXT,
  StepUpCancelledError,
  isStepUpError,
  keepStepUpToken,
  requestStepUpCode,
  takeStepUpToken,
  verifyStepUpCode,
  type StepUpPurpose,
} from "@/api/step-up";
import { StepUpContext, type StepUpRun } from "./step-up-context";

/*
 * StepUpDialog: the reusable email-code confirmation for sensitive payment edits.
 *
 *   const stepUp = useStepUp();   // from ./step-up-context
 *   await stepUp.run("payment_rail", (token) => setRail(..., token), { upfront: true, action: "turn on Razorpay on iOS" });
 *
 * run() tries the write with a cached token (or none). When the server answers
 * step_up_required / step_up_invalid (or `upfront` says it surely will), it opens
 * this dialog: the code is emailed through the admin-step-up function, the person
 * types the 6 digits, admin_verify_step_up returns a token and the write is retried
 * with it. A write that fails for another reason keeps the token (the server
 * didn't burn it) for the next try. Closing the dialog rejects with
 * StepUpCancelledError, which the Money toasts ignore.
 */

interface Ask {
  purpose: StepUpPurpose;
  action: string;
  resolve: (v: { token: string; expiresAt: number }) => void;
  reject: (e: unknown) => void;
}


type Phase =
  | { kind: "sending" }
  | { kind: "sent"; emailMasked: string }
  | { kind: "send_failed"; message: string };

const StepUpDialogView = ({ ask, onDone }: { ask: Ask; onDone: () => void }) => {
  const [phase, setPhase] = useState<Phase>({ kind: "sending" });
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState<{ text: string; needNewCode: boolean } | null>(null);
  const settled = useRef(false);

  const send = useCallback(async () => {
    setPhase({ kind: "sending" });
    setProblem(null);
    setCode("");
    try {
      const r = await requestStepUpCode(ask.purpose);
      setPhase({ kind: "sent", emailMasked: r.emailMasked });
    } catch (e) {
      setPhase({ kind: "send_failed", message: e instanceof Error ? e.message : String(e) });
    }
  }, [ask.purpose]);

  useEffect(() => {
    void send();
  }, [send]);

  const cancel = () => {
    if (checking || settled.current) return;
    settled.current = true;
    ask.reject(new StepUpCancelledError());
    onDone();
  };

  const verify = async () => {
    if (code.length !== 6 || checking) return;
    setChecking(true);
    setProblem(null);
    try {
      const r = await verifyStepUpCode(ask.purpose, code);
      if (r.verified && r.token) {
        settled.current = true;
        ask.resolve({ token: r.token, expiresAt: r.expiresAt ?? Date.now() + 5 * 60_000 });
        onDone();
        return;
      }
      setCode("");
      const left = r.attemptsLeft ?? 0;
      if (r.reason === "wrong_code" && left > 0)
        setProblem({ text: `That code is wrong. ${left} ${left === 1 ? "try" : "tries"} left.`, needNewCode: false });
      else if (r.reason === "expired") setProblem({ text: "That code has expired. Send a new one.", needNewCode: true });
      else setProblem({ text: "Too many wrong tries. Send a new code.", needNewCode: true });
    } catch (e) {
      setProblem({ text: e instanceof Error ? e.message : String(e), needNewCode: false });
    } finally {
      setChecking(false);
    }
  };

  const sending = phase.kind === "sending";
  return (
    <Dialog open onOpenChange={(o) => !o && cancel()}>
      <DialogContent className="max-w-[460px] rounded-2xl bg-card">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 font-serif text-[22px] leading-7">
              <ShieldCheck className="h-5 w-5 text-blue-ink" aria-hidden="true" />
              Confirm with an email code
            </DialogTitle>
            <DialogDescription className="text-ink-2">
              {phase.kind === "sent" ? (
                <>
                  To {ask.action}, enter the 6-digit code we sent to <span className="font-semibold text-foreground">{phase.emailMasked}</span>. It
                  works once and expires in 10 minutes.
                </>
              ) : (
                <>To {ask.action}, we email a 6-digit code to your own sign-in address.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {sending ? (
            <div role="status" aria-busy="true" aria-label="Sending the code" className="flex flex-col items-center gap-3 py-2">
              <span className="sr-only">Sending the code…</span>
              <div className="flex gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Bone key={i} className="h-12 w-11 rounded-md" />
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm text-ink-3">
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> Sending the code…
              </span>
            </div>
          ) : phase.kind === "send_failed" ? (
            <p role="alert" className="rounded-xl border border-rose/30 bg-rose-50 p-3 text-sm text-rose">
              {phase.message}
            </p>
          ) : (
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(v) => setCode(v.replace(/\D/g, ""))}
                aria-label="6-digit email code"
                autoComplete="one-time-code"
                inputMode="numeric"
                disabled={checking || !!problem?.needNewCode}
                autoFocus
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          )}

          {problem && (
            <p role="alert" className="text-center text-sm font-semibold text-rose">
              {problem.text}
            </p>
          )}

          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 text-ink-3">
              <Mail className="h-4 w-4" aria-hidden="true" /> No code? Check spam, or
            </span>
            <Button type="button" variant="link" className="h-10 px-1 font-semibold" disabled={sending || checking} onClick={() => void send()}>
              Send a new code
            </Button>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={cancel} disabled={checking}>
              Cancel
            </Button>
            <Button type="submit" className="h-10 rounded-xl" disabled={phase.kind !== "sent" || code.length !== 6 || checking || !!problem?.needNewCode}>
              {checking ? "Checking…" : "Confirm"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const StepUpProvider = ({ children }: { children: ReactNode }) => {
  const [ask, setAsk] = useState<Ask | null>(null);

  const askFor = useCallback(
    (purpose: StepUpPurpose, action?: string) =>
      new Promise<{ token: string; expiresAt: number }>((resolve, reject) =>
        setAsk({ purpose, action: action || STEP_UP_PURPOSE_TEXT[purpose], resolve, reject })
      ),
    []
  );

  const run: StepUpRun = useCallback(
    async (purpose, write, opts) => {
      // A token: one left from a write that failed for another reason, else a fresh one when asked to.
      let held = takeStepUpToken(purpose);
      if (!held && opts?.upfront) held = await askFor(purpose, opts.action);

      const attempt = async (t: { token: string; expiresAt: number } | null) => {
        try {
          return await write(t?.token ?? null);
        } catch (e) {
          if (t && !isStepUpError(e)) keepStepUpToken(purpose, t.token, t.expiresAt);
          throw e;
        }
      };

      try {
        return await attempt(held);
      } catch (e) {
        if (!isStepUpError(e)) throw e;
        return await attempt(await askFor(purpose, opts?.action));
      }
    },
    [askFor]
  );

  return (
    <StepUpContext.Provider value={{ run }}>
      {children}
      {ask && <StepUpDialogView key={`${ask.purpose}-${ask.action}`} ask={ask} onDone={() => setAsk(null)} />}
    </StepUpContext.Provider>
  );
};
